/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Parameter Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Services
import parameterService from '@nephro/services/parameter-service';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Constants
import {
  CREATE_PARAMETER,
  DELETE_PARAMETER_BY_ID,
  GET_ALL_PARAMETERS,
  GET_MULTIPLE_PARAMETER_BY_KEY,
  GET_PARAMETER_BY_ID,
  GET_PARAMETER_BY_KEY,
  UPDATE_PARAMETER
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get parameters from backend
 * @param { object } action - Action with payload data
 */
export function* getParametersArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(
      parameterService.getParameters,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get parameter info by id
 * @param { object } action - Action with payload data
 */
export function* getParameterById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(parameterService.getParameterById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.parameterNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get parameter info by key
 * @param { object } action - Action with payload data
 */
export function* getParameterByKey(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  const { parameterKey } = data;

  try {
    const response = yield call(
      parameterService.getParameterByKey,
      parameterKey
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.parameterNotFound' || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call process to get multiple parameters by key
 * @param { object } action - Action with payload data
 */
export function* getMultipleParameterByKey(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  const { parameterKeys } = data;

  try {
    const response = yield parameterKeys.map((item) =>
      call(parameterService.getParameterByKey, item)
    );
    yield put(setResponse('success', false, false, response, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.parameterNotFound' || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to create parameter
 * @param { object } action - Action with payload data
 */
export function* createParameter(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { param_key, param_value, param_description } = data;

  try {
    const response = yield call(
      parameterService.createParameter,
      param_key,
      param_value,
      param_description
    );
    yield put(
      setResponse(
        'success',
        'new parameter',
        'paramMg.parameterCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update parameter
 * @param { object } action - Action with payload data
 */
export function* updateParameter(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { param_key, param_value, param_description, id } = data;

  try {
    const response = yield call(
      parameterService.updateParameter,
      param_key,
      param_value,
      param_description,
      id
    );
    yield put(
      setResponse(
        'success',
        'updated parameter',
        'paramMg.parameterUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete parameter info by id
 * @param { object } action - Action with payload data
 */
export function* deleteParameterById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(parameterService.deleteParameterById, id);
    yield put(
      setResponse(
        'success',
        'deleted parameter',
        'paramMg.parameterDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchParameterSaga() {
  yield takeLatest(GET_ALL_PARAMETERS, getParametersArray);
  yield takeLatest(GET_PARAMETER_BY_ID, getParameterById);
  yield takeLatest(CREATE_PARAMETER, createParameter);
  yield takeLatest(UPDATE_PARAMETER, updateParameter);
  yield takeLatest(DELETE_PARAMETER_BY_ID, deleteParameterById);
  yield takeLatest(GET_PARAMETER_BY_KEY, getParameterByKey);
  yield takeLatest(GET_MULTIPLE_PARAMETER_BY_KEY, getMultipleParameterByKey);
}
